import { useEffect, useMemo, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import api from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { Disclaimer } from "@/components/Shared";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const PERIODS = [
  { key: "daily", label: "Daily CPR" },
  { key: "weekly", label: "Weekly CPR" },
  { key: "monthly", label: "Monthly CPR" },
];

const UNIVERSES = [
  { key: "nifty50", label: "Nifty 50 Stocks" },
  { key: "fno", label: "NSE F&O Stocks" },
  { key: "nifty-next-50", label: "Nifty Next 50 Stocks" },
  { key: "nifty-largemidcap-250", label: "Nifty LargeMidcap 250" },
  { key: "nifty-midcap-150", label: "Nifty Midcap 150" },
  { key: "nifty-smallcap-250", label: "Nifty Smallcap 250" },
];

const CLASSES = [
  { key: "narrow", label: "Narrow" },
  { key: "medium", label: "Medium" },
  { key: "wide", label: "Wide" },
];

const dISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseISO = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };
const fmtDate = (iso) => (iso ? parseISO(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const monthRange = (ym) => {
  if (!ym) return { from: "", to: "" };
  const [y, m] = ym.split("-").map(Number);
  const last = String(new Date(y, m, 0).getDate()).padStart(2, "0");
  return { from: `${ym}-01`, to: `${ym}-${last}` };
};

function isoWeekKey(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const day = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((dt - yearStart) / 86400000 + 1) / 7);
  return `${dt.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

function DatePicker({ period, value, onChange, available, restrict = true, placeholder = "Select date", testid }) {
  const [open, setOpen] = useState(false);
  const selDate = value ? parseISO(value) : undefined;
  const availableKeys = useMemo(() => {
    const s = new Set();
    for (const iso of available || []) {
      if (period === "monthly") s.add(iso.slice(0, 7));
      else if (period === "weekly") s.add(isoWeekKey(iso));
      else s.add(iso);
    }
    return s;
  }, [available, period]);
  const restricted = restrict && availableKeys.size > 0;
  const empty = restrict && availableKeys.size === 0;
  const dayAllowed = (d) => {
    const iso = dISO(d);
    if (period === "monthly") return availableKeys.has(iso.slice(0, 7));
    if (period === "weekly") return availableKeys.has(isoWeekKey(iso));
    return availableKeys.has(iso);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testid} className="w-full h-10 rounded-md border border-border px-3 text-sm flex items-center justify-between gap-2 bg-white hover:border-gold transition-colors focus:outline-none focus:ring-2 focus:ring-gold">
          <span className={value ? "text-navy font-medium" : "text-slate-400"}>
            {value ? fmtDate(value) : empty ? "No uploads yet" : placeholder}
          </span>
          <CalendarIcon className="h-4 w-4 text-gold" />
        </button>
      </PopoverTrigger>
      {!empty && (
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            selected={selDate}
            defaultMonth={selDate || (restricted ? parseISO(available[0]) : undefined)}
            onSelect={(d) => { if (!d) return; onChange(dISO(d)); setOpen(false); }}
            disabled={restricted ? (d) => !dayAllowed(d) : undefined}
            initialFocus
          />
        </PopoverContent>
      )}
    </Popover>
  );
}

function MonthPicker({ value, onChange, available, testid }) {
  // value = "YYYY-MM" or ""
  const availMonths = useMemo(() => new Set((available || []).map((d) => d.slice(0, 7))), [available]);
  const years = useMemo(() => {
    const ys = new Set((available || []).map((d) => d.slice(0, 4)));
    ys.add(String(new Date().getFullYear()));
    return [...ys].sort().reverse();
  }, [available]);

  const [extY, extM] = value ? value.split("-") : ["", ""];
  const [year, setYear] = useState(extY);
  const [month, setMonth] = useState(extM);
  useEffect(() => { setYear(extY); setMonth(extM); }, [extY, extM]);

  const effYear = year || years[0] || "";
  const selectCls = "w-full h-10 rounded-md border border-border px-3 text-sm bg-white text-navy hover:border-gold transition-colors focus:outline-none focus:ring-2 focus:ring-gold";

  const pickMonth = (m) => {
    const y = effYear;
    setMonth(m);
    if (!year) setYear(y);
    if (y && m) onChange(`${y}-${m}`);
  };
  const pickYear = (y) => {
    setYear(y);
    if (y && month) onChange(`${y}-${month}`);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold text-slate-500 w-12 shrink-0">Month</span>
        <select data-testid={testid} value={month} onChange={(e) => pickMonth(e.target.value)} className={selectCls}>
          <option value="">Select</option>
          {MONTHS.map((label, i) => {
            const mv = String(i + 1).padStart(2, "0");
            const has = availMonths.has(`${effYear}-${mv}`);
            const dis = availMonths.size > 0 && !has;
            return <option key={mv} value={mv} disabled={dis}>{label}{dis ? " (no data)" : ""}</option>;
          })}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold text-slate-500 w-12 shrink-0">Year</span>
        <select data-testid={`${testid}-year`} value={year} onChange={(e) => pickYear(e.target.value)} className={selectCls}>
          <option value="">Select</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
    </div>
  );
}

function ScannerSection({ period, label, range, universe, universeLabel, classes }) {
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState(null);
  const from = range.from;
  const to = range.to;

  useEffect(() => {
    if (!from || !to) { setDoc(null); return; }
    let alive = true;
    setLoading(true);
    api.get(`/scanner/posts?period=${period}&universe=${universe}&from=${from}&to=${to}`)
      .then((r) => { if (alive) setDoc(r.data); })
      .catch(() => { if (alive) setDoc(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period, universe, from, to]);

  const anyChecked = CLASSES.some((c) => classes[c.key]);
  const items = doc?.items || [];

  const buildHtml = (item) => {
    const sections = item.sections || {};
    if (!Object.keys(sections).length) return item.content_html || "";
    let h = "";
    for (const c of CLASSES) if (classes[c.key] && sections[c.key]) h += sections[c.key];
    h += item.outro || "";
    return h;
  };

  const rangeLabel = from && to
    ? period === "monthly"
      ? parseISO(from).toLocaleDateString("en-IN", { month: "short", year: "numeric" })
      : from === to ? fmtDate(from) : `${fmtDate(from)} – ${fmtDate(to)}`
    : "";

  return (
    <div className="border border-border rounded-lg bg-white overflow-hidden" data-testid={`scanner-section-${period}`}>
      <div className="px-4 sm:px-5 py-4 border-b border-border flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display font-semibold text-navy">
          {label} <span className="text-slate-400 font-body font-normal text-sm">· {universeLabel}</span>
        </h2>
        {doc?.found && (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-subtle border border-border text-navy" data-testid={`scanner-date-badge-${period}`}>
            {rangeLabel}
          </span>
        )}
      </div>
      {loading ? (
        <p className="px-5 py-10 text-center text-slate-400 text-sm">Loading…</p>
      ) : !from || !to ? (
        <p className="px-5 py-10 text-center text-slate-400 text-sm">Select a From and To date on the left to view {label}.</p>
      ) : !doc || !doc.found ? (
        <p className="px-5 py-10 text-center text-slate-400 text-sm">
          No {label} data for {universeLabel} in this date range yet. It appears here after your agent's upload.
        </p>
      ) : !anyChecked ? (
        <p className="px-5 py-10 text-center text-slate-400 text-sm">Select at least one CPR classification (Narrow / Medium / Wide) on the left.</p>
      ) : (
        <div className="p-5 space-y-6" data-testid={`scanner-table-${period}`}>
          {items.map((item) => (
            <div key={item.date}>
              {items.length > 1 && (
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{fmtDate(item.date)}</p>
              )}
              <div className="research-html" dangerouslySetInnerHTML={{ __html: buildHtml(item) }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Scanners() {
  const [meta, setMeta] = useState(null);
  const [checked, setChecked] = useState({ daily: true, weekly: false, monthly: false });
  const [ranges, setRanges] = useState({
    daily: { from: "", to: "" },
    weekly: { from: "", to: "" },
    monthly: { from: "", to: "" },
  });
  const [universe, setUniverse] = useState("nifty50");
  const [classes, setClasses] = useState({ narrow: true, medium: true, wide: true });

  const setRange = (period, field, iso) =>
    setRanges((r) => ({ ...r, [period]: { ...r[period], [field]: iso } }));

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/scanner/meta").then((r) => { if (alive) setMeta(r.data); }).catch(() => {});
    load();
    const iv = setInterval(load, 120000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // Default each period's selection to its latest available date for the chosen universe.
  useEffect(() => {
    if (!meta) return;
    setRanges((prev) => {
      const next = { ...prev };
      for (const p of PERIODS) {
        const avail = meta.availability?.[`${p.key}|${universe}`] || [];
        const cur = prev[p.key];
        if (!avail.length) {
          if (cur.from || cur.to) next[p.key] = { from: "", to: "" };
          continue;
        }
        if (cur.from && cur.to) continue;
        const latest = avail[0];
        next[p.key] = p.key === "monthly" ? monthRange(latest.slice(0, 7)) : { from: latest, to: latest };
      }
      return next;
    });
  }, [meta, universe]);

  const active = PERIODS.filter((p) => checked[p.key]);
  const universeLabel = UNIVERSES.find((u) => u.key === universe)?.label || universe;

  return (
    <div data-testid="scanners-page">
      <PageHero
        eyebrow="Market Data"
        title="CPR Scanners"
        subtitle="Daily, weekly and monthly CPR scans across NSE stock universes. Educational data, not investment advice."
        crumbs={[{ label: "Tools" }, { label: "Scanners" }]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="space-y-6" data-testid="scanner-filters">
          <div>
            <h3 className="font-display font-semibold text-navy mb-3">CPR Period</h3>
            {PERIODS.map((p) => (
              <div key={p.key} className="mb-2">
                <label className="flex items-center gap-2 py-1 text-sm text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked[p.key]}
                    onChange={() => setChecked((c) => ({ ...c, [p.key]: !c[p.key] }))}
                    data-testid={`scanner-period-${p.key}`}
                    className="accent-navy"
                  />
                  {p.label}
                </label>
                {checked[p.key] && (
                  <div className="mt-1.5 ml-6 space-y-1.5">
                    {p.key === "daily" && (
                      <DatePicker
                        period="daily"
                        value={ranges.daily.from}
                        onChange={(iso) => setRanges((r) => ({ ...r, daily: { from: iso, to: iso } }))}
                        available={meta?.availability?.[`daily|${universe}`] || []}
                        testid="scanner-date-daily"
                      />
                    )}
                    {p.key === "weekly" && (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-semibold text-slate-500 w-8 shrink-0">From</span>
                          <DatePicker
                            period="weekly"
                            value={ranges.weekly.from}
                            onChange={(iso) => setRange("weekly", "from", iso)}
                            available={meta?.availability?.[`weekly|${universe}`] || []}
                            restrict={false}
                            placeholder="From date"
                            testid="scanner-from-weekly"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-semibold text-slate-500 w-8 shrink-0">To</span>
                          <DatePicker
                            period="weekly"
                            value={ranges.weekly.to}
                            onChange={(iso) => setRange("weekly", "to", iso)}
                            available={meta?.availability?.[`weekly|${universe}`] || []}
                            restrict={false}
                            placeholder="To date"
                            testid="scanner-to-weekly"
                          />
                        </div>
                      </>
                    )}
                    {p.key === "monthly" && (
                      <MonthPicker
                        value={ranges.monthly.from ? ranges.monthly.from.slice(0, 7) : ""}
                        onChange={(ym) => setRanges((r) => ({ ...r, monthly: monthRange(ym) }))}
                        available={meta?.availability?.[`monthly|${universe}`] || []}
                        testid="scanner-month-picker"
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div>
            <h3 className="font-display font-semibold text-navy mb-3">Stock Universe</h3>
            {UNIVERSES.map((u) => (
              <label key={u.key} className="flex items-center gap-2 py-1 text-sm text-slate-600 cursor-pointer">
                <input
                  type="radio"
                  name="scanner-universe"
                  checked={universe === u.key}
                  onChange={() => setUniverse(u.key)}
                  data-testid={`scanner-universe-${u.key}`}
                  className="accent-navy"
                />
                {u.label}
              </label>
            ))}
          </div>
          <div>
            <h3 className="font-display font-semibold text-navy mb-3">CPR Classification</h3>
            {CLASSES.map((c) => (
              <label key={c.key} className="flex items-center gap-2 py-1 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={classes[c.key]}
                  onChange={() => setClasses((cl) => ({ ...cl, [c.key]: !cl[c.key] }))}
                  data-testid={`scanner-class-${c.key}`}
                  className="accent-navy"
                />
                {c.label}
              </label>
            ))}
          </div>
          {meta?.updated_at && (
            <p className="text-xs text-slate-400">
              Updated · {new Date(meta.updated_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
        </aside>

        <div className="space-y-8 min-w-0" data-testid="scanner-results">
          {active.length === 0 ? (
            <div className="border border-dashed border-border rounded-lg bg-white px-5 py-16 text-center" data-testid="scanner-empty">
              <p className="text-slate-500">Select at least one CPR period on the left to view scanner data.</p>
            </div>
          ) : (
            active.map((p) => (
              <ScannerSection
                key={`${p.key}|${universe}`}
                period={p.key}
                label={p.label}
                range={ranges[p.key]}
                universe={universe}
                universeLabel={universeLabel}
                classes={classes}
              />
            ))
          )}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <Disclaimer />
      </div>
    </div>
  );
}
